import {Component, Injector, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
    DynamicOrderStateDto,
    OrderDto,
    OrderHistoryDto,
    OrderHistoryServiceProxy,
    OrderItemDto,
    OrderServiceProxy
} from '@shared/service-proxies/service-proxies';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';
import {AppComponentBase} from '@shared/common/app-component-base';
import {ActivatedRoute, Router} from '@angular/router';
import {LazyLoadEvent, SelectItem} from 'primeng/api';
import {Table} from 'primeng/table';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import moment, {Moment} from 'moment';

import {delay, filter, repeat, take, takeUntil} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {AppConsts} from '@shared/AppConsts';
import _ from 'lodash';

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent extends AppComponentBase implements OnInit, OnDestroy {

    orderId: string;
    confirmed = true;
    historyList: OrderHistoryDto[];
    @ViewChild('dataTable', {static: true}) dataTable: Table;
    changeRouter$: Subscription;
    products: OrderItemDto[];
    order: OrderDto;
    sortOptions: SelectItem[];
    recordsCount: number;
    rowsAmount = 9;
    remoteServiceBaseUrl: string = AppConsts.remoteServiceBaseUrl;
    noArticlesText: string;
    unsubscribe = new Subject();
    punchoutForm: UntypedFormGroup;
    private _shouldUpdate = true;

    constructor(
        injector: Injector,
        private orderHistoryServiceProxy: OrderHistoryServiceProxy,
        private orderServiceProxy: OrderServiceProxy,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _webshopService: WebshopService
    ) {
        super(injector);

        this.noArticlesText = this.localization.localize('Axilla.Apps.Webshop.App.OrderOverview.NoArticles', 'AxillaAppWebshop');
    }

    //#region Order Availability

    get states(): DynamicOrderStateDto[] | undefined {
        if (!this.order) {
            return undefined;
        }

        return this.order.states;
    }

    //#endregion Order Availability

    get inProgressStatePosition(): number {
        return _.findIndex(this.order.states, (orderState) => orderState.isCurrent);
    }

    ngOnDestroy(): void {
        if (this.changeRouter$) {
            this.changeRouter$.unsubscribe();
        }

        this.unsubscribe.next(true);

    }

    ngOnInit(): void {
        //this._shouldUpdate = false;
        this._initPunchoutSerachForm();

        //this._onRouteChange();
        this.sortOptions = [
            {label: 'Price High to Low', value: '!price'},
            {label: 'Price Low to High', value: 'price'}
        ];
        //this._shouldUpdate = true;

        if (this.isOuBlocked) {
            this.showIsBlockedMessage();
        } else {
            this.checkIsOuBlocked();
        }
    }

    checkState(): boolean {
        if (!this.order) {
            return true;
        }

        if (this.order.state.abbrevations === 'new' || this.order.state.abbrevations === 'waitingForConfirmation') {
            return true;
        }

        return false;
    }

    checkExternalOrderId(): boolean {
        if (!this.order) {
            return false;
        }

        if (this.order.externalOrderId !== 'waiting for order creation') {
            return true;
        }

        return false;
    }

    retry(data: any, index): boolean {
        const url = location.pathname;

        if (data) {
            this.order = data;

            if (data.state.abbrevations === 'new' || data.state.abbrevations === 'waitingForConfirmation') {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    getCurrency() {
        return this._webshopService.getCurrency();
    }

    getHistory() {
        this.orderHistoryServiceProxy.getHistoryByOrderId(this.orderId)
            .subscribe(result => {
                this.historyList = (result as OrderHistoryDto[])
                    .sort((a, b) => a.timeStamp.diff(b.timeStamp));

                this.primengTableHelper.totalRecordsCount = this.historyList.length;
                this.primengTableHelper.records = this.historyList;
                this.primengTableHelper.hideLoadingIndicator();
            });
    }

    loadData(event?: LazyLoadEvent) {
        if (!this.orderId) {
            this.getOrderId();
        }
        this.orderServiceProxy.getOrderItemsByOrder(undefined,
            this.orderId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined)
            .subscribe(x => {
                this.products = x.items;
                this.recordsCount = x.totalCount;
            });
    }

    getOrderId() {
        this.orderId = this._activatedRoute.snapshot.params['id'];
        this.orderServiceProxy.get(this.orderId)
            .pipe(
                delay(5000),
                repeat(),
                filter((data: any, index) => this.retry(data, index)),
                take(1),
                takeUntil(this.unsubscribe)
            )
            .subscribe(x => {
                this.order = x;
            });
    }

    getClass(id: string, isCurrent: boolean) {

        let position = this._checkStatePosition(id);

        if (StatePosition.Current === position) {
            return 'in-progress';
        } else if (StatePosition.After === position) {
            return '';
        } else {
            return 'complete';
        }

    }

    getDate(m: Moment) {
        if (m) {
            return moment(m).format('YYYY-MM-DD HH:mm:ss');
        } else {
            return '-';
        }
    }

    private _initPunchoutSerachForm() {
        this.punchoutForm = new UntypedFormGroup({
            code: new UntypedFormControl(null),
            title: new UntypedFormControl(null),
        });
    }

    private _checkStatePosition(state: string): StatePosition {
        const currentPos = this._getStatePosition(state);
        if (currentPos === this.inProgressStatePosition) {
            return StatePosition.Current;
        } else if (currentPos < this.inProgressStatePosition) {
            return StatePosition.Before;
        } else {
            return StatePosition.After;
        }
    }

    private _getStatePosition(state: string): number {
        let position = 0;

        let arr = _.map(this.order.states, (orderState) => orderState.state);

        position = _.findIndex(arr, (orderState) => _.camelCase(orderState.name) === state);

        return position;
    }

    private _onRouteChange() {
        this.changeRouter$ = this._activatedRoute.params.subscribe(params => {
            if (this._shouldUpdate) {
                this.getOrderId();

            }
        });
    }
}

export enum StatePosition {
    Before,
    Current,
    After
}

